import { acquireApiToken } from '../auth/useAuth';

/**
 * Base URL de l'API. En dev, le proxy Vite redirige /api vers https://localhost:7174.
 * En prod, on définit VITE_API_BASE_URL (ex. "https://comeasy-api.azurewebsites.net").
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/** Erreur API typée — exposée aux pages pour afficher un toast. */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public payload: unknown = null
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Si true, ne pas tenter d'attacher un bearer token (ex. /health). */
  anonymous?: boolean;
  signal?: AbortSignal;
}

/**
 * Wrapper fetch unifié.
 * - Attache automatiquement le bearer token (sauf anonymous: true).
 * - Sérialise/désérialise JSON.
 * - Lève une ApiError typée en cas d'erreur HTTP.
 */
export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, query, anonymous = false, signal } = opts;

  // Construction de l'URL avec query string
  const url = new URL(API_BASE + path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {
    'Accept': 'application/json'
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (!anonymous) {
    try {
      const token = await acquireApiToken();
      headers['Authorization'] = `Bearer ${token}`;
    } catch (err) {
      // Si on est en train de rediriger vers login, on coupe court.
      if (err instanceof Error && err.message.includes('Redirecting')) throw err;
      throw err;
    }
  }

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal
  });

  // 204 No Content → pas de body
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? safeParse(text) : null;

  if (!res.ok) {
    const msg = (payload as { error?: string } | null)?.error
             ?? `Erreur ${res.status}`;
    throw new ApiError(msg, res.status, payload);
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try { return JSON.parse(text); } catch { return text; }
}

// ─── Helpers spécialisés (sucre syntaxique) ─────────────────────
export const apiGet = <T>(path: string, query?: ApiOptions['query']) =>
  api<T>(path, { method: 'GET', query });

export const apiPost = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body });

export const apiPut = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PUT', body });

export const apiDelete = <T>(path: string) =>
  api<T>(path, { method: 'DELETE' });
